import { useCallback } from 'react';

import { useUnit } from 'effector-react';

import { ApiDataResponse } from '../../../../../api/types/response';
import { Search$ } from '../../../../../effector/search';
import { Position } from '../../../../../types/position';
import { SuggestionDetails } from '../../../../../types/suggestion';
import { useDelayedSignal, useMarketLimitSignal, useMarketSignal } from '../../queries';
import { ComplexOrderProcessorType, DelayedSignal, FormFields, LimitSignal, MarketSignal } from '../types';

type UseSignalActionsProps = {
  strategyId: string;
  details: SuggestionDetails | null;
  comment: string;
  setHasChanges: (hasChanges: boolean) => void;
  setPositions: (positions: Position[]) => void;
  positions: Position[];
  editablePosition: Position | null;
  forceClearSearch: () => void;
  onSuccess: (result: ApiDataResponse<unknown>) => void;
  onError: (e: unknown) => void;
  beginSubmission: () => boolean;
};

export const useSignalActions = ({
  strategyId,
  details,
  comment,
  setHasChanges,
  setPositions,
  positions,
  editablePosition,
  forceClearSearch,
  onSuccess,
  onError,
  beginSubmission,
}: UseSignalActionsProps) => {
  const searchStore = useUnit(Search$);

  const { mutate: marketSignal, isPending: isLoadingMarketSignal } = useMarketSignal({
    onSuccess: onSuccess,
    onError: onError,
  });

  const { mutate: marketLimitSignal, isPending: isLoadingMarketLimitSignal } = useMarketLimitSignal({
    onSuccess: onSuccess,
    onError: onError,
  });

  const { mutate: delayedSignal, isPending: isLoadingDelayedSignal } = useDelayedSignal({
    onSuccess: onSuccess,
    onError: onError,
  });

  const createMarketSignal = useCallback(
    (values: FormFields): MarketSignal => {
      let weight = Number(values.size);
      const type = Number(values.type);

      if (type === 2) {
        weight = -1 * Number(values.size);
      } else if (type === 3) {
        weight = 0;
      }

      const signal: MarketSignal = {
        strategyId,
        symbol: searchStore?.details?.symbol as string,
        classCode: searchStore?.details?.classCode as string,
        board: searchStore?.details?.board as string,
        weight: weight,
        comment,
      };

      if (Number(values.stoploss)) {
        signal.stopLoss = Number(values.stoploss);
      }

      if (Number(values.takeprofit)) {
        signal.takeProfit = Number(values.takeprofit);
      }

      if (type) {
        signal.typeSignal = type;
      }

      return signal;
    },
    [strategyId, searchStore, comment],
  );

  const sendMarketSignal = useCallback(
    (values: FormFields) => {
      const signal = createMarketSignal(values);

      if (beginSubmission()) {
        marketSignal(signal);
      }
    },
    [beginSubmission, createMarketSignal, marketSignal],
  );

  const sendDelayedSignal = useCallback(
    (values: FormFields) => {
      const signal: DelayedSignal = createMarketSignal(values);

      signal.tradeType = ComplexOrderProcessorType.Limit;

      if (Number(values.price)) {
        signal.execQuotation = Number(values.price);
      }

      if (Number(values.timeout)) {
        signal.timeout = Number(values.timeout);
      }

      if (beginSubmission()) {
        delayedSignal(signal);
      }
    },
    [beginSubmission, createMarketSignal, delayedSignal],
  );

  const sendMarketLimitSignal = useCallback(
    (values: FormFields) => {
      const signal: LimitSignal = createMarketSignal(values);

      if (Number(values.price)) {
        signal.price = Number(values.price);
      }

      if (Number(values.timeout)) {
        signal.timeout = Number(values.timeout);
      }

      if (beginSubmission()) {
        marketLimitSignal(signal);
      }
    },
    [beginSubmission, createMarketSignal, marketLimitSignal],
  );

  const onAddPositionToPortfolioSignal = useCallback(
    (values: FormFields) => {
      if (details) {
        const params = createMarketSignal(values);
        const position: Position = {
          securityKey: `${details.symbol} ${details.classCode} QUIK`,
          symbol: params.symbol,
          name: details.name,
          comment,
          weight: params.weight,
          openPrice: 0,
          openTime: '',
          classCode: params.classCode,
          currentWeight: params.weight,
          stopLoss: params.stopLoss,
          takeProfit: params.takeProfit,
        };

        const found = positions.find((item) => item.symbol === position.symbol);

        if (found) {
          onError({
            errorMessage: 'Данный инструмент уже добавлен в список',
          });
          return;
        }

        setHasChanges(true);
        setPositions([...positions, position]);
      }
      forceClearSearch();
    },
    [details, createMarketSignal, comment, setHasChanges, setPositions, positions, forceClearSearch, onError],
  );

  const onUpdatePortfolioPositionSignal = useCallback(
    (values: FormFields) => {
      const marketSignal = createMarketSignal(values);
      const updatedPosition: Position = {
        ...(editablePosition as Position),
        weight: marketSignal.weight,
        stopLoss: marketSignal.stopLoss,
        takeProfit: marketSignal.takeProfit,
        currentWeight: marketSignal.weight,
        comment,
      };

      const filteredPositions = positions.filter((item) => item.securityKey !== updatedPosition.securityKey);
      setPositions([...filteredPositions, updatedPosition]);
      setHasChanges(true);
      forceClearSearch();
    },
    [editablePosition, createMarketSignal, comment, setPositions, positions, setHasChanges, forceClearSearch],
  );

  return {
    isLoadingMarketOrDelayedSignal: isLoadingMarketSignal || isLoadingMarketLimitSignal || isLoadingDelayedSignal,
    createMarketSignal,
    sendMarketSignal,
    sendMarketLimitSignal,
    sendDelayedSignal,
    onAddPositionToPortfolioSignal,
    onUpdatePortfolioPositionSignal,
  };
};
